import React, { useMemo } from 'react';
import { BaseSelector, BaseSelectorProps } from '../BaseSelector';
import { Option } from '../CustomDropdown';
import { DOK_MAP } from './DokSelector';
import { useDokRefinement } from './useDokRefinement';
import CustomOptionWithCount from '../CustomOptionWithCount';

interface DokSelectorWithCountsProps
  extends Omit<BaseSelectorProps, 'label' | 'options'> {
  // If true, will attempt to use Algolia refinement context for counts
  enableCounts?: boolean;
  // Manual override for count data (useful for testing or custom data sources)
  countData?: Array<{ level: number; count: number }>;
}

/**
 * Enhanced DokSelector that displays counts from Algolia refinements
 */
export const DokSelectorWithCounts: React.FC<DokSelectorWithCountsProps> = ({
  enableCounts = false,
  countData,
  ...props
}) => {
  const dokRefinement = useDokRefinement();

  const optionsWithCounts = useMemo((): Option[] => {
    // Generate base options
    const baseOptions = Object.entries(DOK_MAP).map(([level, name]) => ({
      label: name, // Just the name for the option label
      value: level,
    }));

    // If counts are not enabled, return base options
    if (!enableCounts) {
      return baseOptions;
    }

    // Add counts from manual data if provided
    if (countData) {
      return baseOptions.map((option) => {
        const countItem = countData.find(
          (item) => item.level === parseInt(option.value, 10),
        );
        return {
          ...option,
          count: countItem?.count,
        };
      });
    }

    // Add counts from refinement context if available
    if (dokRefinement && enableCounts) {
      return baseOptions.map((option) => {
        const level = parseInt(option.value, 10);
        const count = dokRefinement.getCountForLevel(level);
        return {
          ...option,
          count: count > 0 ? count : 0,
        };
      });
    }

    // Fallback to base options if no count data available
    return baseOptions;
  }, [enableCounts, countData, dokRefinement]);

  // Custom label functions to match DokSelector behavior
  const optionLabel = (option: Option) => {
    if (enableCounts && option.count !== undefined) {
      return (
        <CustomOptionWithCount
          value={option.value}
          label={option.label}
          count={option.count}
        />
      );
    }
    return `${option.value} - ${option.label}`;
  };
  const selectedLabel = (selected: Option) =>
    `DOK: ${selected.value} - ${selected.label}`;

  return (
    <BaseSelector
      {...props}
      label="DOK"
      options={optionsWithCounts}
      optionLabel={optionLabel}
      selectedLabel={selectedLabel}
      tooltip="Depth of Knowledge"
    />
  );
};

export default DokSelectorWithCounts;
